import React, {FC} from 'react'
import {Link, useLocation} from 'react-router-dom'
import moment from 'moment'
import {usePermission} from 'src/hooks/usePermission'
import {useAppSelector} from 'src/redux/storeHooks'
import {ConnectedPlatform} from 'src/interfaces/Platform.interface'
import {KTIcon, KTSVG} from 'src/marketConnect/helpers'

type ConnectedPlatformStripe = {
  platform: ConnectedPlatform
  onDelete: () => void
  onTokenRefresh: () => void
  handleOauth: () => void
  isRefreshing: boolean
}

const ConnectPlatformStrip1: FC<ConnectedPlatformStripe> = ({
  platform,
  onDelete,
  onTokenRefresh,
  handleOauth,
  isRefreshing,
}) => {
  const {isDelete} = usePermission()
  const location = useLocation()
  const {fetchedUserAccountData} = useAppSelector((state) => state.userAccountData)
  const isTicketPlatform =
    platform?.isTicketStore === true ||
    [
      platform?.platform_info?.nature,
      (platform as any)?.nature,
      platform?.platform_info?.type,
      platform?.type,
      platform?.code,
      platform?.name,
      platform?.display_name,
      platform?.platform_info?.name,
      platform?.platform_info?.display_name,
    ].some((value) => {
      const normalizedValue = `${value ?? ''}`.toUpperCase()
      return (
        normalizedValue.includes('TICKET') ||
        normalizedValue.includes('HELPDESK') ||
        normalizedValue.includes('HELP_DESK') ||
        normalizedValue.includes('HELP DESK')
      )
    })

  const hasRefreshExpiry = !!platform?.credentials?.refresh_token_expires_date
  const expirationDate = hasRefreshExpiry ? moment(platform?.credentials?.refresh_token_expires_date) : null
  const today = moment()
  const daysUntilExpiration = expirationDate ? expirationDate.diff(today, 'days') : null

  const queryParams = new URLSearchParams(location?.search || '')
  const forceReauthAll =
    (queryParams.get('forceReauth') || queryParams.get('force_reauth') || '').toLowerCase() ===
    'all'

  const forceReauthCodes = (localStorage?.getItem('debug_force_reauth_codes') || '')
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean)

  const backendReauthFlag =
    platform?.is_reauth_required ??
    platform?.platform_info?.is_reauth_required ??
    platform?.credentials?.is_reauth_required

  const isNonOauthPlatform = platform?.platform_info?.isOauth === false
  const nonOauthReauthFlag = isNonOauthPlatform && platform?.is_reauth_required === true
  const oauthReauthFlag = !isNonOauthPlatform && !!backendReauthFlag
  const oauthExpiringSoonReauthFlag =
    platform?.platform_info?.isOauth === true &&
    daysUntilExpiration !== null &&
    daysUntilExpiration <= 7

  const legacyReauthCase =
    ['JUMIAVENDOR'].includes(platform?.code || '') &&
    !!platform?.credentials?.is_token_expiry_notified

  const isVipulUser =
    (fetchedUserAccountData?.email || '').toLowerCase() === 'vipul.constacloud@gmail.com'
  const isEbay = (platform?.code || '').toUpperCase() === 'EBAY'
  const vipulEbayForceReauth = isVipulUser && isEbay && forceReauthAll

  const shouldShowReauthButton =
    oauthReauthFlag ||
    oauthExpiringSoonReauthFlag ||
    nonOauthReauthFlag ||
    legacyReauthCase ||
    forceReauthAll ||
    forceReauthCodes.includes((platform?.code || '').toUpperCase()) ||
    vipulEbayForceReauth

  return (
    <div className='card shadow p-2 my-2'>
      <div className='d-sm-flex flex-md-row justify-content-between align-items-center p-8 border border-light-primary rounded position-relative'>
        <div className='card-title position-absolute top-0 start-0 p-2'>
          {(platform?.isMainStore ||
            platform?.isAccountingStore ||
            platform?.isShippingStore ||
            platform?.isErpStore ||
            platform?.isOmsStore ||
            isTicketPlatform) && (
            <span
              className='badge rounded-pill bg-primary position-absolute top-0 start-0 m-2'
              style={{fontWeight: '600', fontSize: '11px'}}
            >
              {isTicketPlatform
                ? 'HELPDESK'
                : platform?.isMainStore
                ? 'Source Channel'
                : platform?.isShippingStore
                ? 'Shipping Store'
                : platform?.isAccountingStore
                ? 'Accounting Store'
                : platform?.isErpStore
                ? 'ERP Store'
                : platform?.isOmsStore
                ? 'OMS Store'
                : 'Destination Channel'}
            </span>
          )}
        </div>
        <div className='d-flex flex-row align-items-center  mt-6 ml-5'>
          <img
            src={platform.platform_info.logo_ref}
            alt='Channel Logo'
            className='me-2'
            style={{maxWidth: '50px', height: '30px', objectFit: 'contain'}}
          />
          <h4 className='title mb-0'>{platform.display_name}</h4>
        </div>
        <div className='d-md-inline-flex d-flex  mt-sm-6 mt-3 justify-content-sm-start justify-content-between'>
          <span
            className='text-success fw-bold d-flex align-items-center justify-content-center align-self-center fs-5'
            style={{height: '36px', lineHeight: '36px'}}
          >
            Connected
          </span>
          <div className='d-flex align-items-center'>
            {['LAZADA', 'ETSY', 'EBAY'].includes(platform?.code!) &&
            daysUntilExpiration !== null &&
            daysUntilExpiration <= 7 ? (
              <button
                disabled={isRefreshing}
                className='btn btn-sm bg-light-primary text-primary shadow-sm fs-6 fw-bold ms-3'
                onClick={onTokenRefresh}
              >
                {isRefreshing && ['LAZADA', 'ETSY', 'EBAY'].includes(platform?.code!)
                  ? 'Refreshing...'
                  : 'Refresh Token'}
              </button>
            ) : null}

            {shouldShowReauthButton ? (
              <button
                disabled={isRefreshing}
                className='btn btn-sm bg-light-primary text-primary shadow-sm fs-6 fw-bold ms-3'
                onClick={handleOauth}
              >
                {isRefreshing ? 'Re-authenticating...' : 'Re-auth'}
              </button>
            ) : null}

            {/* Admin-only beta cog icon removed per request */}
            {/* {  (['AIMS360','BIGCOMMERCE','SHEIN'].includes(platform.code) || (localStorage.getItem(loggedInAdminAccessToken))) &&
              <OverlayTrigger
              placement="top"
              delay={{ show: 250, hide: 400 }}
              overlay={(props)=><Tooltip id="button-tooltip" {...props}>
              Dynamic Platform Setting
            </Tooltip>}
            >
            <Link to={`/platform-setting-new/${platform.platform_info.id}/${platform.id}`} className='mx-3 '>
              <KTSVG
                path='media/icons/duotune/coding/cod001.svg'
                className='svg-icon-muted svg-icon-2hx'
              />
            </Link>
          </OverlayTrigger>
          } */}
            {
              // !['AIMS360','BIGCOMMERCE'].includes(platform.code) &&
              <Link to={`/channel-settings/${platform.platform_info.id}/${platform.id}`} className='mx-3'>
                <KTSVG
                  path='media/icons/duotune/coding/cod009.svg'
                  className='svg-icon-muted svg-icon-2hx'
                />
              </Link>
            }
            {isDelete && (
              <button 
                className='btn btn-sm btn-danger' 
                onClick={onDelete}
              >
              {/* <KTSVG
                path='media/icons/duotune/general/gen034.svg'
                className='svg-icon-muted svg-icon-2hx'
              /> */}
              <KTIcon iconName='trash' className=' fs-3' />
            </button> 
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConnectPlatformStrip1
